import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import ItemSearchSelect from "@/components/ItemSearchSelect";
import MenuViewer from "@/components/MenuViewer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarPlus, Pencil, Trash2, Loader2, X, Copy } from "lucide-react";
import { toast } from "sonner";
import { todayStr, nowHM, dateStrIST } from "@/lib/utils";

const SERVICE_TIMES = { Breakfast: "08:00", Lunch: "12:30", Snacks: "16:30", Dinner: "19:30" };
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const hmIST = (ms) => new Date(ms).toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
const nowKey = () => `${todayStr()}T${nowHM()}`;
const maxKey = () => { const ms = Date.now() + 48 * 3600000; return `${dateStrIST(ms)}T${hmIST(ms)}`; };
const passed = (m) => `${m.date}T${m.time}` <= nowKey();

const firstSlot = () => {
  const s = Object.keys(SERVICE_TIMES).find((k) => SERVICE_TIMES[k] > nowHM());
  return s ? { date: todayStr(), service: s } : { date: dateStrIST(Date.now() + 86400000), service: "Breakfast" };
};

export default function PlannerView() {
  const { user } = useAuth();
  const [cafeterias, setCafeterias] = useState([]);
  const [items, setItems] = useState([]);
  const [init] = useState(firstSlot);
  const blank = { date: init.date, service: init.service, time: SERVICE_TIMES[init.service], item_id: "", description: "", cafeteria_ids: [] };
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [jumpTo, setJumpTo] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/cafeterias"), api.get("/items")]).then(([c, i]) => {
      setCafeterias(c.data.filter((x) => x.active !== false && x.default_base_kitchen === (user.location || "").trim()));
      setItems(i.data.filter((x) => x.active !== false));
    }).catch(() => {});
  }, [user]);

  const allSelected = cafeterias.length > 0 && form.cafeteria_ids.length === cafeterias.length;
  const maxDate = useMemo(() => dateStrIST(Date.now() + 48 * 3600000), []);

  const toggleCaf = (id) => setForm((f) => ({
    ...f, cafeteria_ids: f.cafeteria_ids.includes(id) ? f.cafeteria_ids.filter((x) => x !== id) : [...f.cafeteria_ids, id],
  }));

  const reset = () => { setForm({ ...blank, date: form.date, service: form.service, time: form.time, cafeteria_ids: form.cafeteria_ids }); setEditingId(null); };

  const save = async () => {
    if (!form.item_id) return toast.error("Pick an item");
    if (!form.cafeteria_ids.length) return toast.error("Select at least one cafeteria");
    const key = `${form.date}T${form.time}`;
    if (key <= nowKey()) return toast.error("That service time has already passed");
    if (key > maxKey()) return toast.error("Menus can only be planned up to 48 hours ahead");
    setSaving(true);
    try {
      if (editingId) await api.put(`/menus/${editingId}`, form);
      else await api.post("/menus", form);
      toast.success(editingId ? "Menu entry updated" : "Added to menu");
      setJumpTo(form.date);
      reset();
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (m) => {
    setEditingId(m.id);
    setForm({ date: m.date, service: m.service, time: m.time, item_id: m.item_id, description: m.description || "", cafeteria_ids: m.cafeteria_ids });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.item_name} from ${m.service}?`)) return;
    try { await api.delete(`/menus/${m.id}`); toast.success("Removed"); if (m.id === editingId) reset(); setReloadKey((k) => k + 1); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [copying, setCopying] = useState(false);
  const copyForward = async () => {
    const from = todayStr();
    const to = dateStrIST(Date.now() + 86400000);
    setCopying(true);
    try {
      const { data } = await api.post("/menus/copy", { from_date: from, to_date: to });
      const skipped = data.skipped_duplicates ? ` (${data.skipped_duplicates} already on tomorrow's menu)` : "";
      if (data.copied) toast.success(`Copied ${data.copied} item(s) to tomorrow${skipped} — edit as needed`);
      else toast.info(`Nothing new to copy${skipped}`);
      setJumpTo(to);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCopying(false);
    }
  };

  const actions = (m) => passed(m) ? null : (
    <div className="flex gap-2 mt-2">
      <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => edit(m)} data-testid="menu-entry-edit-button"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
      <Button size="sm" variant="ghost" className="h-7 gap-1 text-destructive" onClick={() => remove(m)} data-testid="menu-entry-delete-button"><Trash2 className="h-3.5 w-3.5" /> Remove</Button>
    </div>
  );

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className="lg:col-span-5">
          <Card className="p-5 sm:p-6 border-orange-100 dark:border-slate-800 space-y-5" data-testid="planner-form">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-primary" /> {editingId ? "Edit Menu Entry" : "Plan Menu"}</h2>
              {editingId && <Button size="sm" variant="ghost" className="gap-1" onClick={reset} data-testid="planner-cancel-edit-button"><X className="h-4 w-4" /> Cancel</Button>}
            </div>
            <p className="text-xs text-muted-foreground -mt-3">{user?.location} kitchen · plan up to 48 hours ahead</p>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" min={todayStr()} max={maxDate} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="font-mono" data-testid="planner-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="font-mono" data-testid="planner-time-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Service</Label>
              <div className="flex flex-wrap gap-2">
                {Object.keys(SERVICE_TIMES).map((s) => (
                  <button key={s} type="button" data-testid={`planner-service-${s}`}
                    onClick={() => setForm({ ...form, service: s, time: SERVICE_TIMES[s] })}
                    className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${form.service === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Item</Label>
              <ItemSearchSelect items={items} value={form.item_id} onChange={(v) => setForm({ ...form, item_id: v })} testid="planner-item-select" fmtMoney={fmtMoney} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea rows={2} placeholder="e.g. Served with mint chutney and salad" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="planner-description-input" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Cafeterias</Label>
                <button type="button" className="text-xs text-primary font-medium" data-testid="planner-select-all-cafeterias"
                  onClick={() => setForm({ ...form, cafeteria_ids: allSelected ? [] : cafeterias.map((c) => c.id) })}>
                  {allSelected ? "Clear all" : "Select all"}
                </button>
              </div>
              {cafeterias.length === 0 && <p className="text-sm text-muted-foreground">No cafeterias are mapped to {user?.location} kitchen.</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {cafeterias.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:bg-secondary" data-testid="planner-cafeteria-option">
                    <Checkbox checked={form.cafeteria_ids.includes(c.id)} onCheckedChange={() => toggleCaf(c.id)} data-testid={`planner-cafeteria-checkbox-${c.id}`} />
                    <span className="truncate">{c.name.trim()}</span>
                  </label>
                ))}
              </div>
            </div>

            <Button className="w-full gap-2" onClick={save} disabled={saving} data-testid="planner-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editingId ? "Update Entry" : "Add to Menu"}
            </Button>
          </Card>
        </section>

        <section className="lg:col-span-7 space-y-4">
          <Card className="p-4 border-dashed border-primary/40 bg-accent/30 flex flex-wrap items-center justify-between gap-3" data-testid="planner-copy-card">
            <div>
              <p className="font-semibold">Copy today's menu to tomorrow</p>
              <p className="text-xs text-muted-foreground">Copies every item, time, description and cafeteria. Items already on tomorrow's menu are skipped.</p>
            </div>
            <Button className="gap-2" onClick={copyForward} disabled={copying} data-testid="planner-copy-forward-button">
              {copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} Copy to Tomorrow
            </Button>
          </Card>
          <MenuViewer cafeterias={cafeterias} title={`${user?.location} kitchen`} reloadKey={reloadKey} jumpTo={jumpTo} renderActions={actions} />
        </section>
      </main>
    </div>
  );
}
